import { useState } from "react";
import { api, ApiError } from "../../../lib/api";
import type { Asset } from "../../../lib/resources";
import type { LogPartInput } from "../../../lib/api";
import { jobTypeStyle } from "../../../lib/jobType";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";
import { formatDate } from "../../../lib/format";

// "Log Part" on the lift record (Session AG Item 2): the office records a part fitted to the lift
// directly, not only via an engineer's job sheet. Self-contained modal (mirrors SafedTestsButton).
// A part can optionally be linked to an existing job ON THIS LIFT; cost/supplier/notes are captured
// but the lift's Parts History stays cost-free (an operational surface, not a financial one).

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green";
const labelCls = "mb-1 block text-sm text-ele-body";


const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface FormState {
  description: string;
  partCode: string;
  supplier: string;
  unitCost: string;
  quantity: string;
  fittedAt: string;
  jobId: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  description: "",
  partCode: "",
  supplier: "",
  unitCost: "",
  quantity: "1",
  fittedAt: todayIso(),
  jobId: "",
  notes: "",
});

export function LogPartButton({ asset, onChanged }: { asset: Asset; onChanged?: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [jobs, setJobs] = useState<{ id: string; label: string }[]>([]);

  async function openModal() {
    setOpen(true);
    setError(null);
    setForm(emptyForm());
    // Load this lift's jobs for the optional "Job reference" select (best-effort).
    try {
      const r = await api.assets.serviceHistory(asset.id, { limit: 200 });
      setJobs(r.items.map((j) => ({ id: j.id, label: `${j.dateIso ? formatDate(j.dateIso) : "No date"} ${jobTypeStyle(j.type).label}` })));
    } catch {
      setJobs([]);
    }
  }

  async function save() {
    if (!form.description.trim()) {
      setError("Enter a part description.");
      return;
    }
    const qty = Number(form.quantity);
    if (!Number.isFinite(qty) || qty < 1) {
      setError("Enter a quantity of 1 or more.");
      return;
    }
    setBusy(true);
    setError(null);
    const body: LogPartInput = {
      description: form.description.trim(),
      partCode: form.partCode.trim() || null,
      supplier: form.supplier.trim() || null,
      unitCost: form.unitCost.trim() ? Number(form.unitCost) : null,
      quantity: Math.trunc(qty),
      fittedAt: form.fittedAt ? new Date(`${form.fittedAt}T00:00:00Z`).toISOString() : null,
      jobId: form.jobId || null,
      notes: form.notes.trim() || null,
    };
    try {
      await api.assets.createPart(asset.id, body);
      setOpen(false);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "Could not log the part. Check the fields and try again." : "Could not log the part. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Button variant="secondary" size="sm" onClick={openModal}>
        Log Part
      </Button>

      {open && (
        <ModalShell ariaLabel="Log a part" onClose={() => !busy && setOpen(false)} width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
            <h2 className="mb-1 font-heading text-xl text-ele-strong">Log a part</h2>
            <p className="mb-4 text-sm text-ele-body">
              {asset.eleNumber ?? asset.legacyEleNumber ?? "Lift"}
              {asset.site?.name ? `, ${asset.site.name}` : ""}
            </p>

            <div className="space-y-3">
              <div>
                <label className={labelCls} htmlFor="part-desc">Part description</label>
                <input
                  id="part-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className={inputCls}
                  placeholder="e.g. Door operator belt"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="part-code">Part number / reference (optional)</label>
                  <input id="part-code" value={form.partCode} onChange={(e) => setForm((f) => ({ ...f, partCode: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="part-supplier">Supplier (optional)</label>
                  <input id="part-supplier" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className={labelCls} htmlFor="part-cost">Unit cost ex VAT (optional)</label>
                  <input id="part-cost" type="number" min={0} step="0.01" value={form.unitCost} onChange={(e) => setForm((f) => ({ ...f, unitCost: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="part-qty">Quantity</label>
                  <input id="part-qty" type="number" min={1} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="part-date">Date fitted</label>
                  <input id="part-date" type="date" value={form.fittedAt} onChange={(e) => setForm((f) => ({ ...f, fittedAt: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="part-job">Job reference (optional)</label>
                <select id="part-job" value={form.jobId} onChange={(e) => setForm((f) => ({ ...f, jobId: e.target.value }))} className={inputCls}>
                  <option value="">Not linked to a job</option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="part-notes">Notes (optional)</label>
                <textarea id="part-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className={inputCls} />
              </div>
              {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
            </div>

            <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
              >
                Cancel
              </button>
              <Button size="sm" className={MODAL_ACTION_BTN} onClick={save} disabled={busy || !form.description.trim()}>
                {busy ? "Saving..." : "Log part"}
              </Button>
            </div>
        </ModalShell>
      )}
    </div>
  );
}
